/**
 * Heuristics for classifying a markdown link label (`[<label>](url)`) as
 * either an AI-generated attempt to render a timestamp — in which case the
 * renderer should override it with the user's chosen TimestampFormat — or
 * a user-authored custom label (like `[start of segment]`) that must be
 * preserved verbatim.
 *
 * Lives next to the timestamp-mention matchers so both sides of the
 * detection pipeline share one module.
 */

/**
 * True when the trimmed string contains nothing but characters that could
 * plausibly be part of a timestamp rendering — digits, decimal point, sign,
 * approximation marks, whitespace, or the time-syntax letters
 * `{t, n, s, m, u, μ, µ, =}`.
 *
 * Scope: tuned for the ASCII en-US labels the AI emits today. Locale-specific
 * inputs are intentionally NOT recognized as numeric and pass through as
 * custom labels:
 *   - Comma decimal ("1,234 ms"): rejected (no `,` in whitelist).
 *   - NBSP thousands separator ("1 234 ns"): rejected (`\s` excludes NBSP).
 *   - Hyphenated ranges ("10-15s"): accepted as numeric — a known false
 *     positive; AI prompts are instructed to never emit ranges as link labels.
 *
 * Matches (treated as numeric / replace with formatter):
 *   "1771481251468476035"          (bare bigint)
 *   "t=1771481251468476035ns"      (mention)
 *   "1.771481215"                  (seconds-form)
 *   "+1771481426.209115923"        (signed seconds-form)
 *   "1771481251 ms"                (number + unit)
 *
 * Rejects (treated as custom label / preserve):
 *   "start of segment"
 *   "first crash"
 *   "" / whitespace only
 */
export function isLikelyNumericTimestampLabel(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return false;
  }
  if (!/\d/.test(trimmed)) {
    return false;
  }
  return /^[\d\s.+\-~≈tnsmuμµ=]+$/i.test(trimmed);
}
